import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { InvoiceService as BillingInvoiceService, InvoiceStatus } from '@galaxy/billing';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { Notification, NotificationType } from './shared/notification';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class InvoiceService {
  private readonly notification$$: Subject<Notification> = new Subject<Notification>();
  private readonly loading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _loading$: Observable<boolean> = this.loading$$.asObservable();
  private _notifications$: Observable<Notification> = this.notification$$.asObservable();

  constructor(
    private invoiceService: BillingInvoiceService,
    private readonly translateService: TranslateService,
  ) {}

  get notification$(): Observable<Notification> {
    return this._notifications$;
  }

  get loading$(): Observable<boolean> {
    return this._loading$;
  }

  public delete(merchantId: string, invoiceId: string): Observable<boolean> {
    this.loading$$.next(true);
    return this.invoiceService.delete(merchantId, invoiceId).pipe(
      take(1),
      map(() => {
        this.notification$$.next({
          message: 'Successfully deleted invoice.',
          type: NotificationType.SUCCESS,
        });
        return true;
      }),
      catchError(() => {
        this.notification$$.next({
          message: 'Failed to delete invoice.',
          type: NotificationType.ERROR,
        });
        return of(false);
      }),
      finalize(() => this.loading$$.next(false)),
    );
  }

  public changeStatus(
    merchantId: string,
    invoiceId: string,
    invoiceStatus: InvoiceStatus,
    reason: string,
  ): Observable<boolean> {
    let response$: Observable<HttpResponse<null>>;
    switch (invoiceStatus) {
      case InvoiceStatus.PAID:
        this.loading$$.next(true);
        response$ = this.invoiceService.pay(merchantId, invoiceId, reason);
        break;
      case InvoiceStatus.VOID:
        this.loading$$.next(true);
        response$ = this.invoiceService.void(merchantId, invoiceId, reason);
        break;
      default:
        throw new Error('Invalid invoice status');
    }

    return response$.pipe(
      take(1),
      map(() => {
        this.notification$$.next({
          message: this.translateService.instant('INVOICE.TABLE.STATUS_CHANGE_SUCCESS'),
          type: NotificationType.SUCCESS,
        });
        return true;
      }),
      catchError((err) => {
        let message = '';
        if (err?.error?.details[0]?.fieldViolations[0]?.field) {
          if (
            err?.error?.details[0]?.fieldViolations[0]?.field == 'status' &&
            err?.error?.details[0]?.fieldViolations[0]?.description == 'INVALID_STATUS'
          ) {
            message = this.translateService.instant('INVOICE.TABLE.ERRORS.INVALID_STATUS');
          } else if (
            err?.error?.details[0]?.fieldViolations[0]?.field == 'last_payment_status' &&
            err?.error?.details[0]?.fieldViolations[0]?.description == 'PAYMENT_PENDING'
          ) {
            message = this.translateService.instant('INVOICE.TABLE.ERRORS.PAYMENT_PENDING');
          }
        } else {
          message = this.translateService.instant('INVOICE.TABLE.ERRORS.STATUS_CHANGE_FAILED');
        }
        this.notification$$.next({
          message: message,
          type: NotificationType.ERROR,
        });
        return of(false);
      }),
      finalize(() => this.loading$$.next(false)),
    );
  }
}
